import React, { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import "bootstrap/dist/css/bootstrap.min.css";
import "font-awesome/css/font-awesome.min.css";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

function AddRole() {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [nameExists, setNameExists] = useState(false); // For client-side validation cue
  
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  // State to manage form inputs - updated to match new entity structure
  const [formData, setFormData] = useState({
    R_Title: "",
    IsAdmin: false,
    ISBackOffice: false,
    ISStaffAPP: false,
    AllowAdminAccessOntheTill: false,
    AllowTillSettings: false,
    AllowPettyCashes: false,
    AllowFloatAdjustments: false,
    AllowBackOfficeAccessFromTill: false,
    AllowAccessToCustomerList: false,
    AllowTillClosingBlindEndofDay: false,
    AllowAllVoidLines: false,
    AllowTablePlanning: false,
    CanManageSchedule: false,
    CanAccessPOSwithoutClockin: false,
    CanAccessReports: false,
    CanBypassLogin: false,
    AllowBasketDiscounts: false,
    BasketDiscountLimit: null,
    BasketDiscountLimitPercentage: null,
    AllowRefunds: false,
    RefundLimit: null,
    IsPermanentRole: false,
  });
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      R_Title: formData.R_Title || "",
      IsAdmin: Boolean(formData.IsAdmin),
      ISBackOffice: Boolean(formData.ISBackOffice),
      ISStaffAPP: Boolean(formData.ISStaffAPP),
      AllowAdminAccessOntheTill: Boolean(formData.AllowAdminAccessOntheTill),
      AllowTillSettings: Boolean(formData.AllowTillSettings),
      AllowPettyCashes: Boolean(formData.AllowPettyCashes),
      AllowFloatAdjustments: Boolean(formData.AllowFloatAdjustments),
      AllowBackOfficeAccessFromTill: Boolean(formData.AllowBackOfficeAccessFromTill),
      AllowAccessToCustomerList: Boolean(formData.AllowAccessToCustomerList),
      AllowTillClosingBlindEndofDay: Boolean(formData.AllowTillClosingBlindEndofDay),
      AllowAllVoidLines: Boolean(formData.AllowAllVoidLines),
      AllowTablePlanning: Boolean(formData.AllowTablePlanning),
      CanManageSchedule: Boolean(formData.CanManageSchedule),
      CanAccessPOSwithoutClockin: Boolean(formData.CanAccessPOSwithoutClockin),
      CanAccessReports: Boolean(formData.CanAccessReports),
      CanBypassLogin: Boolean(formData.CanBypassLogin),
      AllowBasketDiscounts: Boolean(formData.AllowBasketDiscounts),
      BasketDiscountLimit: formData.BasketDiscountLimit ?? null,
      BasketDiscountLimitPercentage: formData.BasketDiscountLimitPercentage ?? null,
      AllowRefunds: Boolean(formData.AllowRefunds),
      RefundLimit: formData.RefundLimit ?? null,
    });
  }, [formData]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
   
    if (name === 'R_Title') {
      setNameExists(false);
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };


 const handleSubmit = async (options = {}) => {
  const { exitAfterSave = true } = options;

  if (!formData.R_Title.trim()) {
    showErrorToast("Role title is required");
    return false;
  }

  setLoading(true);

  try {
    const rolePayload = {
      CompID: CompID,
      R_Title: formData.R_Title,
      IsAdmin: formData.IsAdmin,
      ISBackOffice: formData.ISBackOffice,
      ISStaffAPP: formData.ISStaffAPP,
      AllowAdminAccessOntheTill: formData.AllowAdminAccessOntheTill,
      AllowTillSettings: formData.AllowTillSettings,
      AllowPettyCashes: formData.AllowPettyCashes,
      AllowFloatAdjustments: formData.AllowFloatAdjustments,
      AllowBackOfficeAccessFromTill: formData.AllowBackOfficeAccessFromTill,
      AllowAccessToCustomerList: formData.AllowAccessToCustomerList,
      AllowTillClosingBlindEndofDay: formData.AllowTillClosingBlindEndofDay,
      AllowAllVoidLines: formData.AllowAllVoidLines,
      AllowTablePlanning: formData.AllowTablePlanning,
      CanManageSchedule: formData.CanManageSchedule,
      CanAccessPOSwithoutClockin: formData.CanAccessPOSwithoutClockin,
      CanAccessReports: formData.CanAccessReports,
      CanBypassLogin: formData.CanBypassLogin,
      AllowBasketDiscounts: formData.AllowBasketDiscounts,
      BasketDiscountLimit: formData.BasketDiscountLimit ? parseFloat(formData.BasketDiscountLimit) : null,
      BasketDiscountLimitPercentage: formData.BasketDiscountLimitPercentage ? parseFloat(formData.BasketDiscountLimitPercentage) : null,
      AllowRefunds: formData.AllowRefunds,
      RefundLimit: formData.RefundLimit ? parseFloat(formData.RefundLimit) : null,
      IsPermanentRole: false, 
    };

    const response = await axiosInstance.post(APIs.ADD_ROLE, rolePayload);


    if (!response.data.status) {
      // Use lowercase 'message' to match JSON serialization
      showErrorToast(response.data.message || "Failed to add role");
      return false;
    }

    showSuccessToast("Role added successfully");
    initialFormSnapshotRef.current = getFormSnapshot();
    if (exitAfterSave) {
      navigate("/role/manageRole");
    }
    return true;
  } catch (error) {
    showErrorToast(
      error.response?.data?.message || "An error occurred while adding the role"  // Also lowercase here
    );
    return false;
  } finally {
    setLoading(false);
  }
}

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/role/manageRole">Manage Roles</Link>
            </li>
            <li className="breadcrumb-item active">Add Role</li>
          </ol>
          <h1 className="page-header mb-0">Add Role</h1>
        </div>
        <SaveActions
          onCancel={() => navigate("/role/manageRole")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>
      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <div className="row mb-3">
          {/* Title Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Title</h4>
              </PanelHeader>
              <PanelBody>
                <div className="col-12">
                  <FormInput
                    label="Title"
                    placeHolder="Enter Role Title"
                    type="text"
                    value={formData.R_Title}
                    onchange={handleInputChange}
                    name="R_Title"
                    // Optional: Visual cue for duplicate (red border)
                    className={nameExists ? "border-danger" : ""}
                    invalid={nameExists} // Pass to FormInput if it supports invalid prop for styling
                  />
                  {nameExists && <small className="text-danger">Role name already exists.</small>}
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Admin Access Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Admin Access</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.IsAdmin}
                        onToggle={(checked) => handleToggleChange(checked, "IsAdmin")}
                        label="Is Admin"
                        name="IsAdmin"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.ISBackOffice}
                        onToggle={(checked) => handleToggleChange(checked, "ISBackOffice")}
                        label="Back Office"
                        name="ISBackOffice"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.ISStaffAPP}
                        onToggle={(checked) => handleToggleChange(checked, "ISStaffAPP")}
                        label="Staff App"
                        name="ISStaffAPP"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAdminAccessOntheTill}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAdminAccessOntheTill")}
                        label="Admin Access On Till"
                        name="AllowAdminAccessOntheTill"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTillSettings}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTillSettings")}
                        label="Allow Till Settings"
                        name="AllowTillSettings"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowPettyCashes}
                        onToggle={(checked) => handleToggleChange(checked, "AllowPettyCashes")}
                        label="Allow Petty Cashes"
                        name="AllowPettyCashes"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowFloatAdjustments}
                        onToggle={(checked) => handleToggleChange(checked, "AllowFloatAdjustments")}
                        label="Allow Float Adjustments"
                        name="AllowFloatAdjustments"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowBackOfficeAccessFromTill}
                        onToggle={(checked) => handleToggleChange(checked, "AllowBackOfficeAccessFromTill")}
                        label="Allow BackOffice Access From Till"
                        name="AllowBackOfficeAccessFromTill"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAccessToCustomerList}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAccessToCustomerList")}
                        label="Allow Access To Customer List"
                        name="AllowAccessToCustomerList"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTillClosingBlindEndofDay}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTillClosingBlindEndofDay")}
                        label="Allow Till Closing Blind End of Day"
                        name="AllowTillClosingBlindEndofDay"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Transaction Options Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Transaction Options</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowAllVoidLines}
                        onToggle={(checked) => handleToggleChange(checked, "AllowAllVoidLines")}
                        label="Allow All Void Lines"
                        name="AllowAllVoidLines"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowTablePlanning}
                        onToggle={(checked) => handleToggleChange(checked, "AllowTablePlanning")}
                        label="Allow Table Planning"
                        name="AllowTablePlanning"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanManageSchedule}
                        onToggle={(checked) => handleToggleChange(checked, "CanManageSchedule")}
                        label="Can Manage Schedule"
                        name="CanManageSchedule"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanAccessPOSwithoutClockin}
                        onToggle={(checked) => handleToggleChange(checked, "CanAccessPOSwithoutClockin")}
                        label="Can Access POS Without Clock-in"
                        name="CanAccessPOSwithoutClockin"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanAccessReports}
                        onToggle={(checked) => handleToggleChange(checked, "CanAccessReports")}
                        label="Can Access Reports"
                        name="CanAccessReports"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.CanBypassLogin}
                        onToggle={(checked) => handleToggleChange(checked, "CanBypassLogin")}
                        label="Can Bypass Login"
                        name="CanBypassLogin"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                </div>
              </PanelBody>
            </Panel>
          </div>

          {/* Till Discount Options Panel */}
          <div className="col-xl-12">
            <Panel className="m-3" data-sortable-id="form-stuff-1">
              <PanelHeader>
                <h4 className="panel-title">Till Discount Options</h4>
              </PanelHeader>
              <PanelBody>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowBasketDiscounts}
                        onToggle={(checked) => handleToggleChange(checked, "AllowBasketDiscounts")}
                        label="Allow Basket Discounts"
                        name="AllowBasketDiscounts"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  {formData.AllowBasketDiscounts && (
                    <>
                      <div className="col-md-4">
                        <FormInput
                          label="Order Discount Limit %"
                          placeHolder="Enter Discount Percentage"
                          type="number"
                          value={formData.BasketDiscountLimitPercentage || ""}
                          onchange={handleInputChange}
                          name="BasketDiscountLimitPercentage"
                        />
                      </div>
                      <div className="col-md-4">
                        <FormInput
                          label="Maximum discount value"
                          placeHolder="Enter Maximum discount value"
                          type="number"
                          value={formData.BasketDiscountLimit || ""}
                          onchange={handleInputChange}
                          name="BasketDiscountLimit"
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="row mt-3">
                  <div className="col-md-4">
                    <div className="d-flex align-items-center m-3">
                      <ToggleSwitch
                        isChecked={formData.AllowRefunds}
                        onToggle={(checked) => handleToggleChange(checked, "AllowRefunds")}
                        label="Allow Refunds"
                        name="AllowRefunds"
                        themeColor={themeColor}
                      />
                    </div>
                  </div>
                  {formData.AllowRefunds && (
                    <div className="col-md-4">
                      <FormInput
                        label="Refund Limit"
                        placeHolder="Enter Refund Limit"
                        type="number"
                        value={formData.RefundLimit || ""}
                        onchange={handleInputChange}
                        name="RefundLimit"
                      />
                    </div>
                  )}
                </div>
              </PanelBody>
            </Panel>
          </div>
        </div>
      </div>
</GridSectionLoader>
    </div>
  );
}

export default AddRole;